<template>
  <section class="duallo-admin-table">
    <header class="duallo-table-header">
      <div>
        <div class="duallo-eyebrow">Tenant Admin</div>
        <h3 class="duallo-table-title">Organizations</h3>
      </div>
      <DisplayOverlayWindowButton ref="newOrganizationWindow" buttonText="Add Organization" iconName="mdi-plus"
        windowTitle="Add Organization">
        <div class="duallo-form add-org-form">
          <header class="duallo-form-header">
            <div class="duallo-eyebrow">New</div>
            <h2 class="duallo-form-title">Organization</h2>
            <p class="duallo-form-subtitle">Create a new organization within this tenant.</p>
          </header>

          <v-form ref="form" @submit.prevent="handleSubmit">
            <div class="duallo-form-fields">
              <v-text-field v-model="name" label="Name" variant="outlined" density="comfortable" hide-details="auto"
                class="duallo-field"
                :rules="[v => !!v || 'Name is required', v => /^[a-z0-9-]+$/.test(v) || 'Lowercase letters, numbers and hyphens only']"
                @input="name = name.toLowerCase().replace(/[^a-z0-9-]/g, '')" />
              <v-text-field v-model="displayName" label="Display Name" variant="outlined" density="comfortable"
                hide-details="auto" class="duallo-field" />
              <v-select v-model="connectionSelected" :items="connections" item-title="name" item-value="id"
                label="Select Connection" variant="outlined" density="comfortable" hide-details="auto"
                class="duallo-field" />
            </div>

            <div class="duallo-form-actions">
              <button type="submit" class="duallo-btn duallo-btn--admin" :disabled="isSubmitting" @click="handleSubmit">
                <v-progress-circular v-if="isSubmitting" indeterminate size="16" width="2" color="#fff" />
                <span v-else>Create Organization</span>
              </button>
            </div>
          </v-form>
        </div>
      </DisplayOverlayWindowButton>
    </header>

    <div class="duallo-table-card">
      <div v-if="isLoading" class="duallo-state-block">
        <div v-for="n in 3" :key="n" class="duallo-row-skeleton" />
      </div>

      <div v-else-if="organizations.length === 0" class="duallo-state-block empty-state">
        <v-icon size="40" color="#4a5a60">mdi-domain</v-icon>
        <p class="empty-title">No organizations</p>
        <p class="empty-subtitle">Add an organization to get started.</p>
      </div>

      <template v-else>
        <div v-if="!isCompact" class="duallo-table-wrap">
          <table class="duallo-data-table">
            <thead>
              <tr>
                <th class="col-name">Name</th>
                <th class="col-id">AuthZero Org ID</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="org in organizations" :key="org.id">
                <td class="cell-name">{{ org.name }}</td>
                <td>
                  <span class="duallo-id-chip">{{ org.authZeroId }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="duallo-card-list">
          <article v-for="org in organizations" :key="org.id" class="duallo-list-card">
            <div class="card-name">{{ org.name }}</div>
            <div class="card-id-row">
              <span class="duallo-eyebrow">AuthZero Org ID</span>
              <span class="duallo-id-chip">{{ org.authZeroId }}</span>
            </div>
          </article>
        </div>
      </template>
    </div>
  </section>
</template>

<script setup>
import { defineOptions, onMounted, ref, computed } from 'vue';
import { useDisplay } from 'vuetify';
import DisplayOverlayWindowButton from '../buttons/DisplayOverlayWindowButton.vue';
import DualloAdminService from '@/services/DualloAdminService';
import { CreateOrganizationRequest } from '@/domain/organization/CreateOrganizationRequest';
import { useAuth0 } from '@auth0/auth0-vue';

defineOptions({ name: 'ManageOrganizationsTable' });

const { smAndDown } = useDisplay();
const isCompact = computed(() => smAndDown.value);

const organizations = ref([]);
const isLoading = ref(true);
const isSubmitting = ref(false);
const form = ref(null);
const connections = ref([]);
const newOrganizationWindow = ref(null);
const { user } = useAuth0();

const name = ref('');
const displayName = ref('');
const authZeroMemberId = ref('');
const connectionSelected = ref(null);

onMounted(async () => {
  try {
    const [organizationData, connectionsData] = await Promise.all([
      DualloAdminService.getOrganizationsByTenant(),
      DualloAdminService.getConnections()
    ]);
    organizations.value = organizationData;
    connections.value = connectionsData;
  } finally {
    isLoading.value = false;
  }
});

const resetForm = () => {
  name.value = '';
  displayName.value = '';
  connectionSelected.value = null;
  form.value?.reset();
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  try {
    authZeroMemberId.value = user.value?.sub;
    const request = new CreateOrganizationRequest(name.value, displayName.value, connectionSelected.value, authZeroMemberId.value);
    await DualloAdminService.createOrganization(request);
    organizations.value = await DualloAdminService.getOrganizationsByTenant();
    resetForm();
    newOrganizationWindow.value?.closeWindow();
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
/* Table-specific bits only — shared shell/form/button styling lives in duallo-components.css. */
.col-name { width: 200px; min-width: 140px; }
.col-id { min-width: 240px; }
.cell-name { color: #fff; font-weight: 600; }

.empty-state { align-items: center; text-align: center; padding: 40px 16px; color: #8aa0aa; }
.empty-title { font-size: 1.05rem; font-weight: 600; color: #cfd8dc; margin: 12px 0 4px; }
.empty-subtitle { font-size: 0.88rem; margin: 0; }

.add-org-form { min-width: min(420px, 80vw); }

.card-name { font-size: 1rem; font-weight: 700; color: #fff; }
.card-id-row { display: flex; flex-direction: column; gap: 4px; }
</style>
